'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Pre, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/frontier'

const SelfConsistency = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.SelfConsistency), { ssr: false })

export default function ReasoningAndAgents() {
  return (
    <Lesson>
      <Step gate="q-cot">
        <H>Thinking out loud</H>
        <P>
          A language model produces each token with a fixed amount of computation. For a hard question, that’s not much — unless
          it writes intermediate steps first. Each written step becomes context the model can attend to, so it can, in effect,
          spend more computation on harder problems. This is <strong>chain-of-thought</strong>.
        </P>
        <Pre>{`Q: A shop sells pens at 3 for $2. How much do 12 pens cost?

Direct:   $6   ✗

Step by step:
  12 pens is 12 ÷ 3 = 4 groups of three.
  Each group costs $2, so 4 × $2 = $8.   ✓`}</Pre>
        <Quiz
          id="q-cot"
          question="Why can writing out reasoning steps improve a language model’s answer?"
          options={[
            { label: 'Each step adds tokens it can attend to, giving it more computation and a place to store intermediate results.', correct: true },
            { label: 'The model gets access to a hidden calculator.', why: 'No tools are involved — it’s the same network, generating more tokens.' },
            { label: 'Longer answers always score higher.', why: 'It’s not about length; it’s about using the extra steps as working memory.' },
          ]}
        />
      </Step>

      <Step>
        <H>Spending compute at answer time</H>
        <P>
          Another way to trade computation for accuracy: ask several times and take a vote (<strong>self-consistency</strong>).
          If each attempt is right more often than any single wrong answer, the majority becomes far more reliable than one
          attempt.
        </P>
        <Figure n="10.11" caption="Each sample is right with probability p (wrong answers are scattered). Majority-vote accuracy climbs with the number of samples — when p is above one half.">
          <SelfConsistency />
        </Figure>
        <P>
          Modern <strong>reasoning models</strong> combine these ideas: they are trained with reinforcement learning to produce
          long internal chains of thought, checking and revising their work before answering. Their accuracy on maths and coding
          keeps improving as they are allowed to “think” longer — a new axis of scaling: <em>test-time compute</em>.
        </P>
      </Step>

      <Step gate="majority">
        <H>Simulate self-consistency</H>
        <Exercise
          id="majority"
          title="Majority voting"
          {...EX.majority}
          timeoutMs={20000}
          hints={[
            <>
              Count with a <C>Map</C>; keep track of the answer with the highest count as you go.
            </>,
            <>
              For accuracy: repeat <C>trials</C> times — gather <C>k</C> answers with <C>askModel(p)</C>, vote, count when the vote is
              “42”.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-agent">
        <H>Agents: models that act</H>
        <P>
          Give a model <strong>tools</strong> — a calculator, a search engine, a code interpreter, the ability to click and type —
          and a loop: decide on an action, run it, read the result, decide again. That’s an <strong>agent</strong>. The model
          supplies judgement; the tools supply abilities it lacks (exact arithmetic, fresh information, effects in the world).
        </P>
        <Exercise id="agent-loop" title="The agent loop" {...EX.agentLoop} />
        <Quiz
          id="q-agent"
          question="In an agent loop, where does the information from a tool call go?"
          options={[
            { label: 'Back into the model’s context, as text it reads before deciding the next action.', correct: true },
            { label: 'Directly into the model’s weights.', why: 'Weights don’t change while it runs. Tool results become part of the context.' },
            { label: 'Straight to the user, bypassing the model.', why: 'The model reads results and decides what to do with them.' },
          ]}
        />
        <Callout kind="warn">
          Agents that act in the real world raise real stakes: a wrong action can delete files, send messages or spend money.
          Careful permissions, human checkpoints and testing matter as much as model quality.
        </Callout>
      </Step>
    </Lesson>
  )
}
